import { Close, RefreshIcon } from 'assets/icons';
import UseContext from 'hooks/useContext';
import { fillText } from './invoiceImportText';

// The keys of the last run's line: an import of a list says how many invoices
// it took (JBWEB-1274), any other run is « dernier import ».
const lastRunKeys = (lastRun) => {
  if (lastRun.scope === 'list' && Number.isInteger(lastRun.invoiceCount)) {
    return lastRun.invoiceCount === 1
      ? {
          plain: [
            'bannerLastListRunOne',
            'dernier import de 1 facture le {date} à {time}',
          ],
          by: [
            'bannerLastListRunOneBy',
            'dernier import de 1 facture le {date} à {time} par {name}',
          ],
        }
      : {
          plain: [
            'bannerLastListRunMany',
            'dernier import de {count} factures le {date} à {time}',
          ],
          by: [
            'bannerLastListRunManyBy',
            'dernier import de {count} factures le {date} à {time} par {name}',
          ],
        };
  }
  return {
    plain: ['bannerLastRun', 'dernier import le {date} à {time}'],
    by: ['bannerLastRunBy', 'dernier import le {date} à {time} par {name}'],
  };
};

/**
 * "Erreur lors de l'importation" above the month's invoices (JBWEB-1266,
 * mockup 2.4), while invoices of the month stay in error. "Voir le journal"
 * opens the invoices to fix (JBWEB-1280); "Tout réessayer" retries them
 * (JBWEB-1267).
 */
const InvoiceImportErrorBanner = ({
  unresolvedErrors,
  lastRun,
  onRetryAll,
  onShowJournal,
  isRetrying = false,
}) => {
  const { language } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceImport.${key}`, fallback);
  if (!unresolvedErrors) return null;

  const count =
    unresolvedErrors === 1
      ? text('bannerCountOne', '1 facture n’a pas pu être importée')
      : fillText(
          text(
            'bannerCountMany',
            '{count} factures n’ont pas pu être importées',
          ),
          {
            count: unresolvedErrors,
          },
        );

  let when = '';
  if (lastRun?.startedAt) {
    const started = new Date(lastRun.startedAt);
    const values = {
      date: started.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
      }),
      time: started.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      name: lastRun.startedBy?.firstname ?? '',
      count: lastRun.invoiceCount,
    };
    const keys = lastRunKeys(lastRun);
    when = fillText(text(...(values.name ? keys.by : keys.plain)), values);
  }

  return (
    <div
      role="alert"
      className="flex items-center gap-3 rounded-lg bg-red-50 px-4 py-3"
    >
      <div className="w-8 h-8 shrink-0 rounded-md bg-red-100 flex items-center justify-center">
        <Close wh={16} color="#DC2626" />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <div className="font-medium text-red-800">
          {text('bannerTitle', 'Erreur lors de l’importation')}
        </div>
        <div className="text-sm text-red-700">
          {when ? `${count} · ${when}` : count}
        </div>
      </div>
      {onShowJournal && (
        <button
          type="button"
          onClick={onShowJournal}
          className="shrink-0 text-sm text-red-800 underline decoration-dotted underline-offset-4 hover:text-red-900"
        >
          {text('showJournal', 'Voir le journal')}
        </button>
      )}
      {onRetryAll && (
        <button
          type="button"
          onClick={onRetryAll}
          disabled={isRetrying}
          className="shrink-0 flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshIcon wh={16} color="#374151" />
          {text('retryAll', 'Tout réessayer')}
        </button>
      )}
    </div>
  );
};

export default InvoiceImportErrorBanner;
